/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useRef, useEffect, useState } from 'react';

interface FilterChipProps {
  label: string;
  value: string | any;
  fieldName: string;
  inputComponent: any;
  inputProps?: any;
  onUpdate: (fieldName: string, value: any) => void;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}

const FilterChip: FC<FilterChipProps> = ({
  label,
  value,
  fieldName,
  inputComponent: InputComponent,
  inputProps = {},
  onUpdate,
  isEditing,
  onEditingChange,
}) => {
  // Format the initial value based on the field name
  const formatInitialValue = (fieldName: string, value: any) => {
    // For requestType, ensure we have the correct format for the react-select component
    if (
      fieldName === 'requestType' &&
      inputProps.options &&
      Array.isArray(value)
    ) {
      return value
        .map((v) => {
          const option = inputProps.options.find((opt: any) => opt.value === v);
          return option || v;
        })
        .filter(Boolean);
    }

    // For requestedBy, ensure we have the correct format
    if (fieldName === 'requestedBy' && inputProps.options && value) {
      const option = inputProps.options.find((user: any) => user.id === value);
      return option ? option.id : value;
    }

    return value;
  };

  const [localValue, setLocalValue] = useState(
    formatInitialValue(fieldName, value),
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset local value when parent value changes
  useEffect(() => {
    setLocalValue(formatInitialValue(fieldName, value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, fieldName, inputProps.options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onEditingChange(false);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, onEditingChange]);

  const handleUpdate = () => {
    if (fieldName === 'requestType' && Array.isArray(localValue)) {
      const values = localValue.map((item) =>
        typeof item === 'object' && item !== null ? item.value : item,
      );
      onUpdate(fieldName, values);
    } else {
      onUpdate(fieldName, localValue);
    }
    onEditingChange(false);
  };

  // Special handler for clearing a filter value
  const handleClear = () => {
    const emptyValue = Array.isArray(localValue) ? [] : '';
    setLocalValue(emptyValue);
    onUpdate(fieldName, emptyValue);
    onEditingChange(false);
  };

  const formatGroupValue = (value: string): string => {
    if (typeof value === 'string') {
      return value
        .split('_')
        .map((word, index) => {
          if (index === 0 && /^[A-Z][0-9]$/.test(word)) {
            return word;
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    }
    return value;
  };

  const getDisplayValue = () => {
    // For requestedBy field, find the user and display their name
    if (fieldName === 'requestedBy' && inputProps.options) {
      const selectedUser = inputProps.options.find(
        (user: any) => user.id === localValue,
      );
      if (selectedUser) {
        return inputProps.renderOption(selectedUser).props.children;
      }
    }

    // For assignedGroup field, format the value
    if (fieldName === 'assignedGroup' && typeof localValue === 'string') {
      return formatGroupValue(localValue);
    }

    // Handle different types of values
    if (typeof localValue === 'string') {
      return localValue || 'All';
    }

    if (Array.isArray(localValue)) {
      if (!localValue || localValue.length === 0) return 'All';

      return localValue
        .map((val) => {
          if (typeof val === 'object' && val !== null) {
            return val.label || val.value;
          }
          return typeof val === 'string' ? formatGroupValue(val) : val;
        })
        .join(', ');
    }

    // Default fallback
    return localValue?.toString() || 'All';
  };

  const displayValue = getDisplayValue();

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">{label}:</span>
        <span className="max-w-32 truncate">{displayValue}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            <InputComponent
              input={{
                name: fieldName,
                value: localValue || '',
                onChange: (e: any) => {
                  const newValue = e?.target?.value ?? e;
                  setLocalValue(newValue);
                },
              }}
              {...inputProps}
            />
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterChip;
